import {
  get_headers, get_project_id,
  get_token,
  ACTION_TYPES,
  COMPLETION_TYPES,
  SECTION_SCOPE,
  QUESTION_SCOPE,
  TRANSITION_TYPES,
  ASSIGNMENT_TYPES,
  MATCH_REQUIREMENTS,
  MODAL_STEPS
} from '@/utils/constants/constants.js';

import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from "@/utils/mixins/formatting_mixins";
import { section_access_mixin } from '@/utils/mixins/section_access_mixin';
import { status_reviewers_mixin } from '@/utils/mixins/status_reviewers_mixin';
import { workflow_status_mixin } from '@/utils/mixins/workflow_status_mixin';
import { transition_rules_mixin } from '@/utils/mixins/transition_rules_mixin';
import { transition_conditions_mixin } from '@/utils/mixins/transition_conditions_mixin';
import { questions_conditions_mixin } from '@/utils/mixins/questions_conditions_mixin';
import { roles_actions_mixin } from '@/utils/mixins/roles_actions_mixin';
import { search_and_users_mixin } from '@/utils/mixins/search_and_users_mixin';
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net';
import NavigationBackButton from '@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue';

export default {
  name: 'ManageApprovalRules',
  mixins: [
    alert_mixin,
    formatting_mixins,
    section_access_mixin,
    status_reviewers_mixin,
    search_and_users_mixin,
    workflow_status_mixin,
    transition_rules_mixin,
    transition_conditions_mixin,
    questions_conditions_mixin,
    roles_actions_mixin
  ],
  components: {
    NavigationBackButton
  },
  data: () => ({
    headers: get_headers(),
    token: get_token(),
    project_id: get_project_id(),
    workflow_id: sessionStorage.getItem('workflow_id'),
    workflow_name: sessionStorage.getItem('workflow_name'),
    form_id: sessionStorage.getItem('form_id'),
    form_name: sessionStorage.getItem('form_name'),
    entity_type_name: sessionStorage.getItem('entity_type_name'),
    active_tab: 'reviewers',
    loading: true,
    current_status: null,
    show_status_reviewer_modal: false,
    show_transition_rule_modal: false,
    editing_step: MODAL_STEPS.FLOW,
    status_reviewer_step: MODAL_STEPS.FLOW,
    status_reviewer_data: null,
    transition_rule_step: MODAL_STEPS.FLOW,
    transition_rule_data: null,
    editing_rule: null,
    editing_status_index: null,
    editing_reviewer_index: null
  }),
  computed: {
    filtered_modal_users() {
      const user_search = Reflect.get(this.status_reviewer_data, 'user_search');
      if (!user_search) {
        const available_users = Reflect.get(this.status_reviewer_data, 'available_users');
        const result = available_users || [];
        return result;
      }
      const search_term = user_search.toLowerCase();
      const filtered_result = this.filter_users_by_search_term(search_term);
      return filtered_result;
    },
    is_edit_ready_to_update() {
      const is_ready = this.editing_step === MODAL_STEPS.CONFIRM &&
        this.editing_rule &&
        Reflect.get(this.editing_rule, 'to_status_id');
      return is_ready;
    }
  },
  methods: {
    get_section_name(section_id) {
      const section = this.section_list.find(s => Reflect.get(s, 'id') === section_id);
      const section_name = section?.name || section?.section_name || `Section ${section_id}`;
      return section_name;
    },
    open_add_status_reviewers_modal(status) {
      Reflect.set(this, 'current_status', status);
      Reflect.set(this, 'show_status_reviewer_modal', true);
      Reflect.set(this, 'status_reviewer_step', MODAL_STEPS.FLOW);
      this.initialize_status_reviewer_data();
    },
    open_add_transition_rules_modal(status) {
      Reflect.set(this, 'current_status', status);
      Reflect.set(this, 'show_transition_rule_modal', true);
      Reflect.set(this, 'transition_rule_step', MODAL_STEPS.FLOW);
      this.initialize_transition_rule_data();
    },
    select_assignment_type(type) {
      Reflect.set(this.status_reviewer_data, 'assignment_type', type);
      Reflect.set(this, 'status_reviewer_step', MODAL_STEPS.TYPE);
    },
    async get_users_by_role_for_modal() {
      const selected_role = Reflect.get(this.status_reviewer_data, 'selected_role');
      if (!selected_role) {
        Reflect.set(this.status_reviewer_data, 'available_users', []);
        return;
      }
      const temp_status = this.create_temp_status_for_users();
      await this.get_users_by_role_for_status_reviewer(temp_status, 0);
      this.update_available_users_from_temp(temp_status);
      this.update_status_reviewer_step();
    },
    go_to_next_status_step() {
      if (Reflect.get(this, 'status_reviewer_step') === MODAL_STEPS.CONFIRM) {
        this.save_status_reviewer_assignment();
      } else {
        const current_step = Reflect.get(this, 'status_reviewer_step');
        Reflect.set(this, 'status_reviewer_step', current_step + 1);
      }
    },
    go_to_previous_status_step() {
      const current_step = Reflect.get(this, 'status_reviewer_step');
      if (current_step > MODAL_STEPS.FLOW) {
        Reflect.set(this, 'status_reviewer_step', current_step - 1);
      }
    },
    get_role_name(role_id) {
      if (!role_id) {
        const empty_string = '';
        return empty_string;
      }
      const role = (this.roles || []).find(r => Reflect.get(r, 'id') === role_id);
      const role_name = role ? Reflect.get(role, 'role') : '';
      return role_name;
    },
    async save_status_reviewer_assignment() {
      const current_status = Reflect.get(this, 'current_status');
      if (!current_status) {
        this.show_error("No status selected");
        return;
      }
      const temp_reviewer = this.create_temp_reviewer_object();
      await this.handle_reviewer_assignment(temp_reviewer);
      await this.load_status_reviewers(current_status);
      Reflect.set(this, 'show_status_reviewer_modal', false);
    },
    close_status_reviewer_modal() {
      Reflect.set(this, 'show_status_reviewer_modal', false);
      Reflect.set(this, 'current_status', null);
      Reflect.set(this, 'status_reviewer_step', MODAL_STEPS.FLOW);
      this.initialize_status_reviewer_data();
    },
    select_transition_type(type) {
      Reflect.set(this.transition_rule_data, 'transition_type_id', type);
    },
    go_to_next_transition_step() {
      if (!this.validate_current_step()) return;
      if (Reflect.get(this, 'transition_rule_step') === MODAL_STEPS.CONFIRM) {
        this.save_transition_rule_from_modal();
      } else {
        const current_step = Reflect.get(this, 'transition_rule_step');
        Reflect.set(this, 'transition_rule_step', current_step + 1);
      }
    },
    validate_current_step() {
      const current_step = Reflect.get(this, 'transition_rule_step');
      if (current_step === MODAL_STEPS.FLOW) {
        const step_one_result = this.validate_step_one();
        return step_one_result;
      }
      if (current_step === MODAL_STEPS.TYPE) {
        const step_two_result = this.validate_step_two();
        return step_two_result;
      }
      const default_result = true;
      return default_result;
    },
    go_to_previous_transition_step() {
      const current_step = Reflect.get(this, 'transition_rule_step');
      if (current_step > MODAL_STEPS.FLOW) {
        Reflect.set(this, 'transition_rule_step', current_step - 1);
      }
    },
    add_condition_to_modal() {
      const new_condition = this.create_empty_condition();
      const conditions = Reflect.get(this.transition_rule_data, 'conditions');
      conditions.push(new_condition);
    },
    get_available_statuses(from_status_id) {
      if (!this.workflow_statuses) {
        const empty_array = [];
        return empty_array;
      }
      const filtered_statuses = this.workflow_statuses.filter(status =>
        status && Reflect.get(status, 'id') !== from_status_id
      );
      return filtered_statuses;
    },
    get_status_name(status_id) {
      if (!status_id || !this.workflow_statuses) {
        const unknown_status = 'Unknown Status';
        return unknown_status;
      }
      const status = this.workflow_statuses.find(s =>
        Reflect.get(s, 'id') === parseInt(status_id, 10)
      );
      const status_name = status ? Reflect.get(status, 'status') : 'Unknown Status';
      return status_name;
    },
    remove_condition_from_modal(index) {
      const conditions = Reflect.get(this.transition_rule_data, 'conditions');
      if (index >= 0 && index < conditions.length) {
        conditions.splice(index, 1);
      }
    },
    async save_transition_rule_from_modal() {
      const current_status = Reflect.get(this, 'current_status');
      if (!current_status) {
        this.show_error("No status selected");
        return;
      }
      const status_index = this.find_status_index();
      if (status_index === -1) {
        this.show_error("Status not found");
        return;
      }
      const reviewer = this.prepare_reviewer_for_save();
      const conditions = Reflect.get(reviewer, 'conditions');
      const prepared_conditions = this.prepare_conditions_for_save(conditions);
      Reflect.set(reviewer, 'conditions', prepared_conditions);
      await this.save_transition_rule(reviewer, status_index);
      await this.get_transition_rules(current_status);
      Reflect.set(this, 'show_transition_rule_modal', false);
    },
    close_transition_rule_modal() {
      Reflect.set(this, 'show_transition_rule_modal', false);
      Reflect.set(this, 'current_status', null);
      Reflect.set(this, 'transition_rule_step', MODAL_STEPS.FLOW);
      this.initialize_transition_rule_data();
    },
    get_existing_transitions_count(status) {
      const existing_transition_rules = Reflect.get(status, 'existing_transition_rules');
      const count = existing_transition_rules ? existing_transition_rules.length : 0;
      return count;
    },
    has_assigned_reviewers(status) {
      if (!status || !Reflect.get(status, 'status_reviewers')) {
        const false_result = false;
        return false_result;
      }
      const reviewers = Array.isArray(Reflect.get(status, 'status_reviewers')) ?
        Reflect.get(status, 'status_reviewers') : [];
      const has_assigned = reviewers.some(this.has_assigned_data);
      return has_assigned;
    },
    get_assigned_reviewers(status) {
      if (!status || !Reflect.get(status, 'status_reviewers')) {
        const empty_array = [];
        return empty_array;
      }
      const reviewers = Array.isArray(Reflect.get(status, 'status_reviewers')) ?
        Reflect.get(status, 'status_reviewers') : [];
      const assigned_reviewers = reviewers.filter(this.has_assigned_data);
      return assigned_reviewers;
    },
    async load_questions_for_selected_sections(condition) {
      const selected_sections_for_questions = this.ensure_array(
        Reflect.get(condition, 'selected_sections_for_questions')
      );
      const selected_questions = this.ensure_array(
        Reflect.get(condition, 'selected_questions')
      );
      Reflect.set(condition, 'selected_sections_for_questions', selected_sections_for_questions);
      Reflect.set(condition, 'selected_questions', selected_questions);

      const mixin = this.find_mixin_method('load_questions_for_selected_sections');
      if (mixin) {
        await mixin.call(this, condition);
      }
    },
    async load_all_status_data() {
      if (!this.workflow_statuses || this.workflow_statuses.length === 0) {
        Reflect.set(this, 'loading', false);
        return;
      }
      await this.process_all_statuses();
      Reflect.set(this, 'loading', false);
    },
    go_to_next_edit_step() {
      if (!this.validate_edit_step()) return;
      if (Reflect.get(this, 'editing_step') === MODAL_STEPS.CONFIRM) {
        this.update_transition_rule();
      } else {
        const current_step = Reflect.get(this, 'editing_step');
        Reflect.set(this, 'editing_step', current_step + 1);
      }
    },
    go_to_previous_edit_step() {
      const current_step = Reflect.get(this, 'editing_step');
      if (current_step > MODAL_STEPS.FLOW) {
        Reflect.set(this, 'editing_step', current_step - 1);
      }
    },
    validate_edit_step() {
      const editing_rule = Reflect.get(this, 'editing_rule');
      if (!editing_rule) {
        this.show_error("No rule data found");
        const false_result = false;
        return false_result;
      }
      const current_step = Reflect.get(this, 'editing_step');
      if (current_step === MODAL_STEPS.FLOW) {
        const step_one_result = this.validate_edit_step_one();
        return step_one_result;
      }
      if (current_step === MODAL_STEPS.TYPE) {
        const step_two_result = this.validate_edit_step_two();
        return step_two_result;
      }
      if (current_step === MODAL_STEPS.CONDITIONS) {
        const step_three_result = this.validate_edit_step_three();
        return step_three_result;
      }
      const default_result = true;
      return default_result;
    },
    async open_edit_rule_modal(status_index, reviewer_index) {
      const status = this.get_status_by_index(status_index);
      if (!status) {
        this.show_error('Status not found');
        return;
      }
      const reviewer = this.get_reviewer_by_index(status, reviewer_index);
      if (!reviewer) {
        this.show_error('Reviewer not found');
        return;
      }
      this.set_edit_modal_indices(status_index, reviewer_index);
      const reviewer_data = JSON.parse(JSON.stringify(reviewer));
      await this.prepare_edit_modal_data(reviewer_data);
      this.ensure_edit_modal_defaults(reviewer_data);
      Reflect.set(this, 'editing_rule', reviewer_data);
      Reflect.set(this, 'editing_step', MODAL_STEPS.FLOW);
      this.initialize_edit_search_maps();
      this.$forceUpdate();
    },
    initialize_edit_search_maps() {
      const editing_rule = Reflect.get(this, 'editing_rule');
      if (editing_rule && Reflect.get(editing_rule, 'conditions')) {
        const conditions = Reflect.get(editing_rule, 'conditions');
        conditions.forEach(this.initialize_condition_search_map);
      }
    },
    async load_questions_for_selected_sections_edit(condition) {
      const selected_sections_for_questions = this.ensure_array(
        Reflect.get(condition, 'selected_sections_for_questions')
      );
      const selected_questions = this.ensure_array(
        Reflect.get(condition, 'selected_questions')
      );
      Reflect.set(condition, 'selected_sections_for_questions', selected_sections_for_questions);
      Reflect.set(condition, 'selected_questions', selected_questions);

      const mixin = this.find_mixin_method('load_questions_for_selected_sections');
      if (mixin) {
        await mixin.call(this, condition);
      }
    },
    reset_edit_modal() {
      Reflect.set(this, 'editing_rule', null);
      Reflect.set(this, 'editing_status_index', null);
      Reflect.set(this, 'editing_reviewer_index', null);
      Reflect.set(this, 'editing_step', MODAL_STEPS.FLOW);
    },
    add_condition(reviewer) {
      if (!Reflect.get(reviewer, 'conditions')) {
        this.$set(reviewer, 'conditions', []);
      }
      const new_condition = this.create_empty_condition_for_reviewer();
      const conditions = Reflect.get(reviewer, 'conditions');
      conditions.push(new_condition);
    },
    initialize_status_reviewer_data() {
      const initial_data = {
        assignment_type: null,
        selected_role: null,
        selected_users: [],
        user_search: '',
        available_users: []
      };
      Reflect.set(this, 'status_reviewer_data', initial_data);
    },
    initialize_transition_rule_data() {
      const initial_data = {
        to_status_id: null,
        transition_type_id: null,
        min_approvals: 1,
        conditions: []
      };
      Reflect.set(this, 'transition_rule_data', initial_data);
    },
    filter_users_by_search_term(search_term) {
      const available_users = Reflect.get(this.status_reviewer_data, 'available_users') || [];
      const filtered_users = available_users.filter(user => {
        if (!user) {
          const false_result = false;
          return false_result;
        }
        const first_name = Reflect.get(user, 'first_name') || '';
        const last_name = Reflect.get(user, 'last_name') || '';
        const email = Reflect.get(user, 'email') || '';
        const matches = first_name.toLowerCase().includes(search_term) ||
          last_name.toLowerCase().includes(search_term) ||
          email.toLowerCase().includes(search_term);
        return matches;
      });
      return filtered_users;
    },
    create_temp_status_for_users() {
      const current_status = Reflect.get(this, 'current_status');
      const temp_status = {
        id: Reflect.get(current_status, 'id'),
        status_reviewers: [{
          selected_role: Reflect.get(this.status_reviewer_data, 'selected_role'),
          available_users: [],
          filtered_users: [],
          user_search: ""
        }]
      };
      return temp_status;
    },
    update_available_users_from_temp(temp_status) {
      const temp_reviewer = Reflect.get(temp_status, 'status_reviewers')[0];
      const available_users = Reflect.get(temp_reviewer, 'available_users') || [];
      Reflect.set(this.status_reviewer_data, 'available_users', available_users);
      Reflect.set(this.status_reviewer_data, 'user_search', "");
    },
    update_status_reviewer_step() {
      const assignment_type = Reflect.get(this.status_reviewer_data, 'assignment_type');
      if (assignment_type === ASSIGNMENT_TYPES.USER) {
        Reflect.set(this, 'status_reviewer_step', MODAL_STEPS.CONDITIONS);
      } else {
        Reflect.set(this, 'status_reviewer_step', MODAL_STEPS.CONFIRM);
      }
    },
    create_temp_reviewer_object() {
      const assignment_type = Reflect.get(this.status_reviewer_data, 'assignment_type');
      const selected_users = Reflect.get(this.status_reviewer_data, 'selected_users');
      const required_count = assignment_type === ASSIGNMENT_TYPES.USER ? selected_users.length : 1;
      const temp_reviewer = {
        assignment_type: assignment_type,
        selected_role: Reflect.get(this.status_reviewer_data, 'selected_role'),
        selected_users: selected_users,
        required_reviewer_count: required_count
      };
      return temp_reviewer;
    },
    async handle_reviewer_assignment(temp_reviewer) {
      const assignment_type = Reflect.get(this.status_reviewer_data, 'assignment_type');
      const current_status = Reflect.get(this, 'current_status');
      if (assignment_type === ASSIGNMENT_TYPES.ROLE) {
        await this.handle_role_assignment(current_status, temp_reviewer);
      } else {
        await this.handle_user_assignment(current_status, temp_reviewer);
      }
    },
    create_empty_condition() {
      const empty_condition = {
        action_type: '',
        required_role: '',
        approvals_required: 1,
        selected_sections: [],
        completion_type: COMPLETION_TYPES.COMPLETE,
        questions_search_map: {},
        selected_sections_for_questions: [],
        selected_questions: [],
        action_search: '',
        selected_actions: [],
        selected_action: [],
        section_scope: SECTION_SCOPE.ALL,
        question_scope: QUESTION_SCOPE.ALL
      };
      return empty_condition;
    },
    validate_step_one() {
      const to_status_id = Reflect.get(this.transition_rule_data, 'to_status_id');
      if (!to_status_id) {
        this.show_error("Please select a target status");
        const false_result = false;
        return false_result;
      }
      const true_result = true;
      return true_result;
    },
    validate_step_two() {
      const transition_type_id = Reflect.get(this.transition_rule_data, 'transition_type_id');
      if (!transition_type_id) {
        this.show_error("Please select a transition type");
        const false_result = false;
        return false_result;
      }
      if (transition_type_id === TRANSITION_TYPES.MANUAL) {
        const min_approvals = Reflect.get(this.transition_rule_data, 'min_approvals');
        if (!min_approvals || min_approvals < 1) {
          this.show_error("Please enter a valid number of minimum approvals (at least 1)");
          const false_result = false;
          return false_result;
        }
      }
      const true_result = true;
      return true_result;
    },
    find_status_index() {
      const current_status = Reflect.get(this, 'current_status');
      const workflow_statuses = Reflect.get(this, 'workflow_statuses') || [];
      const status_index = workflow_statuses.findIndex(s =>
        s && Reflect.get(s, 'id') === Reflect.get(current_status, 'id')
      );
      return status_index;
    },
    prepare_reviewer_for_save() {
      const reviewer = {
        to_status_id: Reflect.get(this.transition_rule_data, 'to_status_id'),
        transition_type_id: Reflect.get(this.transition_rule_data, 'transition_type_id'),
        min_approvals: Reflect.get(this.transition_rule_data, 'min_approvals') || 1,
        conditions: Reflect.get(this.transition_rule_data, 'conditions'),
        show_condition_ui: false,
        match_requirement: MATCH_REQUIREMENTS.ALL,
        selected_action: [],
        section_scope: SECTION_SCOPE.ALL,
        question_scope: QUESTION_SCOPE.ALL,
        selected_sections: [],
        selected_questions: [],
        selected_sections_for_questions: [],
        required_role: '',
        completion_type: COMPLETION_TYPES.COMPLETE,
        approvals_required: 1,
      };
      return reviewer;
    },
    prepare_conditions_for_save(conditions) {
      const prepared_conditions = conditions.map(condition => {
        const selected_actions = this.ensure_array(Reflect.get(condition, 'selected_actions'));
        const selected_sections = this.ensure_array(Reflect.get(condition, 'selected_sections'));
        const selected_questions = this.ensure_array(Reflect.get(condition, 'selected_questions'));
        const selected_sections_for_questions = this.ensure_array(
          Reflect.get(condition, 'selected_sections_for_questions')
        );
        const section_scope = Reflect.get(condition, 'section_scope') || SECTION_SCOPE.ALL;
        const question_scope = Reflect.get(condition, 'question_scope') || QUESTION_SCOPE.ALL;

        const prepared_condition = Object.assign({}, condition);
        Reflect.set(prepared_condition, 'selected_action', selected_actions);
        Reflect.set(prepared_condition, 'selected_sections', selected_sections);
        Reflect.set(prepared_condition, 'selected_questions', selected_questions);
        Reflect.set(prepared_condition, 'selected_sections_for_questions', selected_sections_for_questions);
        Reflect.set(prepared_condition, 'section_scope', section_scope);
        Reflect.set(prepared_condition, 'question_scope', question_scope);

        return prepared_condition;
      });
      return prepared_conditions;
    },
    has_assigned_data(reviewer) {
      if (!reviewer) {
        const false_result = false;
        return false_result;
      }
      const assigned_users = Reflect.get(reviewer, 'assigned_users');
      const has_assigned_users = assigned_users &&
        Array.isArray(assigned_users) &&
        assigned_users.length > 0;
      const assigned_role = Reflect.get(reviewer, 'assigned_role');
      const has_assigned_role = assigned_role &&
        Reflect.get(assigned_role, 'id') &&
        Reflect.get(assigned_role, 'name');
      const result = has_assigned_users || has_assigned_role;
      return result;
    },
    find_mixin_method(method_name) {
      const mixins = Reflect.get(this.$options, 'mixins');
      const mixin = mixins.find(m =>
        Reflect.get(m, 'methods') &&
        Reflect.get(Reflect.get(m, 'methods'), method_name)
      );
      const method = mixin ? Reflect.get(Reflect.get(mixin, 'methods'), method_name) : null;
      return method;
    },
    async process_all_statuses() {
      const workflow_statuses = Reflect.get(this, 'workflow_statuses');
      for (const status of workflow_statuses) {
        if (status && Reflect.get(status, 'id')) {
          await this.load_status_reviewers(status);
          await this.get_transition_rules(status);
        }
      }
    },
    validate_edit_step_one() {
      const to_status_id = Reflect.get(this.editing_rule, 'to_status_id');
      if (!to_status_id) {
        this.show_error("Please select a target status");
        const false_result = false;
        return false_result;
      }
      const true_result = true;
      return true_result;
    },
    validate_edit_step_two() {
      const transition_type_id = Reflect.get(this.editing_rule, 'transition_type_id');
      if (!transition_type_id) {
        this.show_error("Please select a transition type");
        const false_result = false;
        return false_result;
      }
      if (transition_type_id === TRANSITION_TYPES.MANUAL) {
        const min_approvals = Reflect.get(this.editing_rule, 'min_approvals');
        if (!min_approvals || min_approvals < 1) {
          this.show_error("Please enter a valid number of minimum approvals (at least 1)");
          const false_result = false;
          return false_result;
        }
      }
      const true_result = true;
      return true_result;
    },
    validate_edit_step_three() {
      const editing_rule_conditions = Reflect.get(this.editing_rule, 'conditions');
      if (!editing_rule_conditions) {
        return true;
      }

      for (let condition_index = 0; condition_index < Reflect.get(editing_rule_conditions, 'length'); condition_index++) {
        const current_condition = Reflect.get(editing_rule_conditions, condition_index);
        const is_condition_valid = this.validate_edit_condition(current_condition, condition_index);
        if (!is_condition_valid) {
          return false;
        }
      }

      return true;
    },
    validate_edit_condition(condition, index) {
      const action_type = Reflect.get(condition, 'action_type');
      if (!action_type) {
        this.show_error("Please select an action type for condition " + (index + 1));
        const false_result = false;
        return false_result;
      }
      const selected_action = Reflect.get(condition, 'selected_action');
      if (!selected_action || selected_action.length === 0) {
        this.show_error("Please select at least one action for condition " + (index + 1));
        const false_result = false;
        return false_result;
      }
      const true_result = true;
      return true_result;
    },
    get_status_by_index(index) {
      const workflow_statuses = Reflect.get(this, 'workflow_statuses');
      const status = Reflect.get(workflow_statuses, index);
      return status;
    },
    get_reviewer_by_index(status, index) {
      const reviewers = Reflect.get(status, 'reviewers');
      if (!reviewers || reviewers.length <= index) {
        const null_result = null;
        return null_result;
      }
      const reviewer = Reflect.get(reviewers, index);
      return reviewer;
    },
    set_edit_modal_indices(status_index, reviewer_index) {
      Reflect.set(this, 'editing_status_index', status_index);
      Reflect.set(this, 'editing_reviewer_index', reviewer_index);
    },
    async prepare_edit_modal_data(reviewer_data) {
      const conditions = Reflect.get(reviewer_data, 'conditions');
      if (conditions && conditions.length > 0) {
        const sections_needing_questions = this.extract_sections_needing_questions(conditions);
        const processed_conditions = await this.process_conditions(conditions);
        Reflect.set(reviewer_data, 'conditions', processed_conditions);
        if (sections_needing_questions.size > 0) {
          await this.load_questions_for_sections(Array.from(sections_needing_questions));
        }
      }
    },
    extract_sections_needing_questions(conditions) {
      const sections_needing_questions = new Set();
      conditions.forEach(condition => {
        const action_type = Reflect.get(condition, 'action_type');
        const selected_sections_for_questions = this.ensure_array(
          Reflect.get(condition, 'selected_sections_for_questions')
        );

        if (action_type === ACTION_TYPES.QUESTION_COMPLETION &&
          selected_sections_for_questions.length > 0) {
          selected_sections_for_questions.forEach(section_id => {
            sections_needing_questions.add(section_id);
          });
        }
      });
      return sections_needing_questions;
    },
    async process_conditions(conditions) {
      const processed_conditions = await Promise.all(
        conditions.map(async condition => {
          const selected_actions = this.get_selected_actions_from_condition(condition);
          const selected_sections_for_questions_raw = this.ensure_array(
            Reflect.get(condition, 'selected_sections_for_questions')
          );
          const selected_questions_raw = this.ensure_array(
            Reflect.get(condition, 'selected_questions')
          );
          const selected_sections_for_questions = this.parse_ids_to_numbers(selected_sections_for_questions_raw);
          const selected_questions = this.parse_ids_to_numbers(selected_questions_raw);

          const processed_condition = Object.assign({}, condition);
          Reflect.set(processed_condition, 'selected_sections', this.ensure_array(Reflect.get(condition, 'selected_sections')));
          Reflect.set(processed_condition, 'selected_sections_for_questions', selected_sections_for_questions);
          Reflect.set(processed_condition, 'selected_questions', selected_questions);
          Reflect.set(processed_condition, 'selected_actions', selected_actions);
          Reflect.set(processed_condition, 'selected_action', selected_actions);
          Reflect.set(processed_condition, 'sections_search', '');
          Reflect.set(processed_condition, 'questions_search_map', {});
          Reflect.set(processed_condition, 'action_search', '');
          Reflect.set(processed_condition, 'required_role', Reflect.get(condition, 'required_role') || '');
          Reflect.set(processed_condition, 'completion_type', Reflect.get(condition, 'completion_type') || COMPLETION_TYPES.COMPLETE);
          Reflect.set(processed_condition, 'approvals_required', Reflect.get(condition, 'approvals_required') || 1);

          return processed_condition;
        })
      );
      return processed_conditions;
    },
    get_selected_actions_from_condition(condition) {
      let selected_actions = [];
      const condition_selected_actions = Reflect.get(condition, 'selected_actions');
      const condition_selected_action = Reflect.get(condition, 'selected_action');

      if (condition_selected_actions) {
        selected_actions = this.ensure_array(condition_selected_actions);
      } else if (condition_selected_action) {
        selected_actions = this.ensure_array(condition_selected_action);
      }

      const parsed_actions = this.parse_ids_to_numbers(selected_actions);
      return parsed_actions;
    },
    parse_ids_to_numbers(ids) {
      const parsed_ids = ids.map(id => parseInt(id, 10));
      const filtered_ids = parsed_ids.filter(id => !isNaN(id));
      return filtered_ids;
    },
    async load_questions_for_sections(section_ids) {
      const temp_condition = {
        selected_sections_for_questions: section_ids,
        questions_search_map: {}
      };
      await this.load_questions_for_selected_sections_edit(temp_condition);
    },
    ensure_edit_modal_defaults(reviewer_data) {
      const match_requirement = Reflect.get(reviewer_data, 'match_requirement');
      if (!match_requirement) {
        Reflect.set(reviewer_data, 'match_requirement', MATCH_REQUIREMENTS.ALL);
      }
      const transition_type_id = Reflect.get(reviewer_data, 'transition_type_id');
      const min_approvals = Reflect.get(reviewer_data, 'min_approvals');
      if (transition_type_id === TRANSITION_TYPES.MANUAL && !min_approvals) {
        Reflect.set(reviewer_data, 'min_approvals', 1);
      }
    },
    initialize_condition_search_map(condition) {
      const selected_sections_for_questions = this.ensure_array(
        Reflect.get(condition, 'selected_sections_for_questions')
      );
      if (selected_sections_for_questions.length > 0) {
        let questions_search_map = Reflect.get(condition, 'questions_search_map');
        if (!questions_search_map) {
          questions_search_map = {};
          Reflect.set(condition, 'questions_search_map', questions_search_map);
        }
        selected_sections_for_questions.forEach(section_id => {
          const search_value = Reflect.get(questions_search_map, section_id);
          if (!search_value) {
            Reflect.set(questions_search_map, section_id, '');
          }
        });
      }
    },
    create_empty_condition_for_reviewer() {
      const empty_condition = {
        action_type: '',
        selected_action: '',
        section_scope: SECTION_SCOPE.ALL,
        selected_sections: [],
        question_scope: QUESTION_SCOPE.ALL,
        selected_sections_for_questions: [],
        selected_questions: [],
        required_role: '',
        completion_type: COMPLETION_TYPES.COMPLETE,
        approvals_required: 1
      };
      return empty_condition;
    },
    ensure_array(value) {
      if (!value) {
        const empty_array = [];
        return empty_array;
      }
      if (Array.isArray(value)) {
        return value;
      }
      const single_array = [value];
      return single_array;
    },
    on_action_type_change(condition) {
      const action_type = Reflect.get(condition, 'action_type');
      if (action_type !== 'question_completion') {
        Reflect.set(condition, 'selected_sections_for_questions', []);
        Reflect.set(condition, 'selected_questions', []);
      }
    },
    add_condition_in_edit() {
      if (!Reflect.get(this.editing_rule, 'conditions')) {
        this.$set(this.editing_rule, 'conditions', []);
      }
      const new_condition = this.create_empty_condition_for_reviewer();
      const conditions = Reflect.get(this.editing_rule, 'conditions');
      conditions.push(new_condition);
    },
    remove_condition_in_edit(index) {
      const conditions = Reflect.get(this.editing_rule, 'conditions');
      if (index >= 0 && index < conditions.length) {
        conditions.splice(index, 1);
      }
    }
  },
  watch: {
    reviewer_count(new_count) {
      this.update_reviewers(new_count);
    }
  },
  async mounted() {
    await this.get_user_roles();
    await this.manage_form_workflow();
    this.get_sections();
    this.get_all_actions();
    this.fetch_transition_types();
    const workflow_statuses = Reflect.get(this, 'workflow_statuses');
    if (workflow_statuses && workflow_statuses.length > 0) {
      await this.load_all_status_data();
    }
    Reflect.set(this, 'loading', false);
  }
};